import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Star, 
  Shield, 
  Phone, 
  Video, 
  Lock, 
  Sparkles,
  ChevronDown,
  ArrowRight,
  Headphones,
  Mail,
  MapPin,
  Heart
} from 'lucide-react';

const TellMeApp = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const [randomFaces, setRandomFaces] = useState([]);
  const [callActive, setCallActive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [activeCallFace, setActiveCallFace] = useState(null);
  const [listeners, setListeners] = useState([]);

  // Separate name arrays for each gender
  const maleNames = ['Rahul', 'Amit', 'Vikram', 'Rohan', 'Arjun', 'Karan', 'Dev', 'Aditya', 'Harsh', 'Varun', 'Raj', 'Akash', 'Nitin', 'Sanjay', 'Ravi'];
  const femaleNames = ['Priya', 'Sneha', 'Ananya', 'Kavya', 'Meera', 'Ishita', 'Riya', 'Pooja', 'Nisha', 'Tanya', 'Neha', 'Sita', 'Radha', 'Anjali', 'Deepika'];

  // Scroll function that works
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Random face generation function with proper gender matching
  const generateRandomFace = (forceGender = null) => {
    const gender = forceGender || (Math.random() > 0.5 ? 'men' : 'women');
    const faceId = Math.floor(Math.random() * 99) + 1;
    const nameArray = gender === 'men' ? maleNames : femaleNames;
    const name = nameArray[Math.floor(Math.random() * nameArray.length)];

    return {
      gender,
      name,
      image: `https://randomuser.me/api/portraits/${gender}/${faceId}.jpg`,
      fallbackImage: `https://ui-avatars.com/api/?name=${name}&background=random&color=fff&size=128`
    };
  };

  // Generate random locations
  const generateRandomLocation = () => {
    const cities = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Ahmedabad', 'Jaipur', 'Kochi', 'Gurgaon', 'Noida', 'Indore', 'Lucknow', 'Nagpur'];
    return cities[Math.floor(Math.random() * cities.length)];
  };

  // Generate random interests
  const generateRandomInterests = () => {
    const allInterests = ['Music', 'Movies', 'Books', 'Travel', 'Food', 'Sports', 'Art', 'Technology', 'Dancing', 'Photography', 'Gaming', 'Fitness', 'Cooking', 'Reading'];
    const numInterests = Math.floor(Math.random() * 3) + 2;
    const shuffled = allInterests.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, numInterests);
  };

  // Generate random faces for call simulation grid
  const generateRandomFaces = () => {
    const faces = [];
    for (let i = 0; i < 8; i++) {
      const faceData = generateRandomFace();
      faces.push({
        id: i,
        ...faceData,
        isOnline: Math.random() > 0.2, // 80% online rate
        location: generateRandomLocation(),
        age: Math.floor(Math.random() * 12) + 18, // 18 to 29 years old
        interests: generateRandomInterests()
      });
    }
    return faces;
  };

  // Generate specific supportive listeners
  const generateListeners = () => {
    return [
      {
        ...generateRandomFace('women'),
        specialties: ['Anxiety', 'Relationship'],
        online: true
      },
      {
        ...generateRandomFace('men'),
        specialties: ['Loneliness', 'Career Chat'],
        online: true
      },
      {
        ...generateRandomFace('women'),
        specialties: ['Confessions', 'Breakups'],
        online: true
      },
      {
        ...generateRandomFace('women'),
        specialties: ['Dating Tips', 'Stress'],
        online: true
      }
    ];
  };

  // Initialize random faces and listeners on component mount
  useEffect(() => {
    setRandomFaces(generateRandomFaces());
    setListeners(generateListeners());

    // Refresh profiles periodically
    const interval = setInterval(() => {
      if (!callActive && !connecting) {
        setRandomFaces(generateRandomFaces());
      }
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callActive, connecting]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Initiate call to a specific user
  const initiateCall = (face) => {
    setActiveCallFace(face);
    setConnecting(true);
    setCallActive(false);

    // Scroll up to hero section where simulator is located
    scrollToSection('home');

    setTimeout(() => {
      setConnecting(false);
      setCallActive(true);
    }, 1500); // 1.5s connection delay
  };

  // Start random voice/video call
  const startRandomCall = () => {
    const onlineUsers = randomFaces.filter(f => f.isOnline);
    const chosenFace = onlineUsers.length > 0 
      ? onlineUsers[Math.floor(Math.random() * onlineUsers.length)] 
      : generateRandomFace();
    
    // Supplement chosenFace details if generated on the fly
    if (!chosenFace.age) {
      chosenFace.age = Math.floor(Math.random() * 10) + 18;
      chosenFace.location = generateRandomLocation();
      chosenFace.interests = generateRandomInterests();
      chosenFace.isOnline = true;
    }

    initiateCall(chosenFace);
  };

  // End active call simulation
  const endCall = () => {
    setCallActive(false);
    setConnecting(false);
    setActiveCallFace(null);
  };

  // Image component with fallback loading
  const RandomUserImage = ({ face, size = "w-16 h-16 sm:w-20 sm:h-20", className = "" }) => {
    const [imageError, setImageError] = useState(false);
    const defaultFace = React.useMemo(() => generateRandomFace(), []);
    const safeFace = face || defaultFace;
    const imageUrl = imageError ? safeFace.fallbackImage : safeFace.image;

    return (
      <div className={`${size} rounded-full overflow-hidden ${className}`}>
        <img
          src={imageUrl}
          alt={safeFace.name}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
          loading="lazy"
        />
      </div>
    );
  };

  // Smartphone simulator video call UI
  const VideoCallInterface = ({ face, isActive, isConnecting }) => {
    const defaultFace = React.useMemo(() => generateRandomFace(), []);
    const safeFace = face || defaultFace;

    return (
      <div className={`w-48 h-72 sm:w-56 sm:h-80 md:w-64 md:h-96 bg-slate-900 rounded-[2.5rem] p-2.5 sm:p-3.5 shadow-2xl mx-auto border-4 border-slate-800 ${isActive ? 'ring-4 ring-indigo-500/50' : ''}`}>
        <div className="w-full h-full bg-gradient-to-b from-slate-900 to-indigo-950 rounded-2xl relative overflow-hidden flex flex-col justify-between p-3">
          
          {/* Top Notch Status */}
          <div className="flex justify-between items-center z-10">
            <span className="text-[9px] bg-slate-950/60 px-2 py-0.5 rounded-full text-indigo-300 font-semibold border border-indigo-500/10">
              {isConnecting ? 'Connecting...' : isActive ? 'Live Call' : 'Idle'}
            </span>
            {safeFace.isOnline && (
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse border border-white"></div>
            )}
          </div>

          {/* User Visual Card */}
          <div className="absolute inset-0 w-full h-full">
            <img 
              src={safeFace.image} 
              alt={safeFace.name} 
              className={`w-full h-full object-cover transition-all duration-700 ${isConnecting ? 'blur-md scale-105 brightness-50' : isActive ? 'brightness-90' : 'brightness-50 grayscale-[30%]'}`} 
              onError={(e) => { e.target.src = safeFace.fallbackImage; }}
            />
            {/* Dark overlay gradients */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/10 to-slate-950/40"></div>
          </div>

          {/* Connecting Loading Ripple */}
          {isConnecting && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="relative flex items-center justify-center">
                <div className="absolute w-16 h-16 bg-indigo-500/30 rounded-full animate-ping"></div>
                <div className="absolute w-24 h-24 bg-purple-500/20 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                  <Phone className="text-white animate-bounce" size={18} />
                </div>
              </div>
            </div>
          )}

          {/* Idle Placeholder */}
          {!isActive && !isConnecting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-4 text-center space-y-3">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center text-indigo-400 border border-indigo-500/30">
                <Video size={20} />
              </div>
              <div>
                <p className="text-xs font-bold text-white">Simulator Offline</p>
                <p className="text-[9px] text-gray-400">Click "Start Random Call" below or call a user from the grid.</p>
              </div>
            </div>
          )}

          {/* Active Calling User Details */}
          {(isActive || isConnecting) && (
            <div className="z-10 text-center pb-2 mt-auto">
              <h3 className="font-bold text-white text-sm tracking-wide drop-shadow-md">
                {safeFace.name}, {safeFace.age || 22}
              </h3>
              <p className="text-[10px] text-indigo-300 font-medium flex items-center justify-center gap-1 drop-shadow">
                <MapPin size={10} /> {safeFace.location || 'India'}
              </p>
              
              {isActive && (
                <div className="flex flex-wrap justify-center gap-1 mt-1.5 max-h-8 overflow-hidden">
                  {(safeFace.interests || ['Music', 'Movies']).slice(0, 2).map((interest, idx) => (
                    <span key={idx} className="text-[8px] bg-slate-950/75 text-indigo-300 px-2 py-0.5 rounded-full border border-indigo-500/20">
                      {interest}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Interactive Call Control Buttons */}
          {(isActive || isConnecting) && (
            <div className="z-10 flex justify-center space-x-3.5 pt-2 border-t border-white/10 mt-1">
              <button 
                onClick={endCall} 
                className="w-9 h-9 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors shadow-lg cursor-pointer"
                title="Hang Up"
              >
                <Phone className="text-white transform rotate-135" size={14} />
              </button>
              <button 
                className="w-9 h-9 bg-slate-950/80 hover:bg-indigo-600/50 rounded-full flex items-center justify-center transition-colors border border-white/10"
                onClick={() => alert('Mute microphone toggled inside simulator.')}
              >
                <Headphones className="text-white" size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const reviews = [
    {
      date: "04/12/2026",
      rating: 5,
      text: "TellMe provided a great private space. Connecting with random listeners is seamless, and talking to someone helped me clear my head. 100% anonymous as advertised."
    },
    {
      date: "03/28/2026",
      rating: 5,
      text: "The audio quality is crystal clear! I really appreciate the toxicity screening. It makes meeting random people a lot safer and keeps abusers out. Highly recommended."
    },
    {
      date: "03/15/2026",
      rating: 5,
      text: "Love how I can express myself freely. The supportive listeners in the app are very understanding. The UI is clean, modern, and does not require complex signup details."
    },
    {
      date: "02/22/2026",
      rating: 5,
      text: "Amazing video calling! I use it whenever I feel alone. Finding listeners who speak my native language is simple. Safe space to confessions."
    }
  ];

  const faqs = [
    { 
      question: "What is TellMe App?", 
      answer: "TellMe is a secure, user-friendly mobile voice and video chat application. It allows you to connect instantly and anonymously with random users and empathetic listeners across India." 
    },
    { 
      question: "Is TellMe App really Anonymous?", 
      answer: "Yes. TellMe is built with confidentiality in mind. Your phone number is only used for OTP security during login and is never shared with other users. All matching calls are completely private, and we do not log user identities or locations." 
    },
    { 
      question: "Who is a Listener on TellMe?", 
      answer: "Listeners are compassionate community members who are registered on TellMe to provide emotional support and a friendly ear. They offer a safe, non-judgmental space to talk about stress, breakups, or life challenges." 
    },
    { 
      question: "How do you prevent spam and harassment?", 
      answer: "We deploy real-time AI filters that screen audio/chat prompts for toxic words, bullying, and adult content. You can also block and report any user instantly from the active call interface, leading to permanent hardware bans for violators." 
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 font-sans text-gray-100 overflow-x-hidden selection:bg-indigo-500 selection:text-white">
      {/* Background ambient lighting */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[600px] -z-10 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.12)_0,rgba(0,0,0,0)_70%)]"></div>
      
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-slate-950/85 backdrop-blur-lg border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-tr from-indigo-500 via-purple-500 to-fuchsia-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm sm:text-lg">T</span>
            </div>
            <span className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">
              TellMe
            </span>
          </div>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-7 lg:space-x-8">
            <button onClick={() => scrollToSection('home')} className="hover:text-indigo-400 transition-colors text-sm font-semibold bg-transparent border-0 cursor-pointer text-gray-300">Home</button>
            <button onClick={() => scrollToSection('online-users')} className="hover:text-indigo-400 transition-colors text-sm font-semibold bg-transparent border-0 cursor-pointer text-gray-300">Online Grid</button>
            <button onClick={() => scrollToSection('features')} className="hover:text-indigo-400 transition-colors text-sm font-semibold bg-transparent border-0 cursor-pointer text-gray-300">Features</button>
            <button onClick={() => scrollToSection('reviews')} className="hover:text-indigo-400 transition-colors text-sm font-semibold bg-transparent border-0 cursor-pointer text-gray-300">Reviews</button>
            <button onClick={() => scrollToSection('faqs')} className="hover:text-indigo-400 transition-colors text-sm font-semibold bg-transparent border-0 cursor-pointer text-gray-300">FAQs</button>
          </nav>

          <a 
            href="https://play.google.com/store/apps/details?id=com.avente.tellme"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-full text-xs sm:text-sm font-bold shadow-lg shadow-indigo-600/20 transition-all"
          >
            Get App
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-28 pb-16 sm:pt-36 sm:pb-24 px-4 relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 md:gap-12 items-center">
          
          {/* Left Text */}
          <div className="space-y-6 sm:space-y-8 order-2 lg:order-1" data-animate id="hero-text">
            <div className="inline-flex items-center px-3.5 py-1.5 rounded-full border border-indigo-500/20 bg-indigo-500/10 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5 text-indigo-400 mr-2 animate-pulse" />
              <span className="text-[11px] sm:text-xs font-bold text-indigo-300 uppercase tracking-wider">Meet Random People Online</span>
            </div>

            <h1 className={`text-3.5xl sm:text-5xl md:text-6xl font-black leading-tight text-white tracking-tight transition-all duration-1000 ${isVisible['hero-text'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              TellMe: Connect <br/>
              One Real <br/>
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-fuchsia-400 bg-clip-text text-transparent">
                Conversation
              </span> at a Time
            </h1>

            <p className={`text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed transition-all duration-1000 delay-300 ${isVisible['hero-text'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Experience genuine connections on TellMe — the secure voice and video calling app. Speak anonymously with random people across India, share moments, and find caring listeners who listen and support.
            </p>

            <div className={`flex flex-col sm:flex-row gap-3.5 transition-all duration-1000 delay-500 ${isVisible['hero-text'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <button
                onClick={startRandomCall}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 px-7 py-3.5 rounded-full font-bold flex items-center justify-center space-x-2 transform hover:scale-105 transition-all duration-300 shadow-xl shadow-indigo-600/20 text-xs sm:text-sm"
              >
                <Video size={16} />
                <span>Start Random Call</span>
              </button>
              <button 
                onClick={() => scrollToSection('online-users')}
                className="border border-slate-700 hover:border-indigo-400 bg-slate-900/50 px-7 py-3.5 rounded-full font-semibold flex items-center justify-center space-x-2 transform hover:scale-105 transition-all duration-300 text-xs sm:text-sm"
              >
                <Users size={16} />
                <span>Browse Online Users</span>
              </button>
            </div>

            {/* Ratings summary */}
            <div className="flex items-center space-x-5 pt-3">
              <div className="flex text-amber-400 space-x-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="text-xs text-gray-400">
                <span className="font-bold text-white">4.7/5 rating</span> from Google Play store downloads.
              </p>
            </div>
          </div>

          {/* Right Phone Mockup (Calling Simulator) */}
          <div className="order-1 lg:order-2 flex justify-center relative">
            <VideoCallInterface 
              face={activeCallFace} 
              isActive={callActive} 
              isConnecting={connecting}
            />
            {/* Ambient behind-simulator glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -z-10"></div>
          </div>

        </div>
      </section>

      {/* Online Users Grid Section */}
      <section id="online-users" className="py-16 sm:py-20 px-4 border-t border-slate-900" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
            <h2 className="text-2.5xl sm:text-4xl font-black mb-3 text-white tracking-tight">Connect with Random Listeners</h2>
            <p className="text-xs sm:text-sm text-gray-400">Meet supportive, caring individuals who speak your language. Click "Call Now" to test matching.</p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
            {randomFaces.length > 0 ? randomFaces.slice(0, 8).map((face, index) => (
              <div key={index} className="bg-slate-900/40 p-4 rounded-2xl border border-slate-800 text-center flex flex-col justify-between hover:bg-slate-900/60 transition-all group duration-300">
                
                {/* Portrait */}
                <div className="relative mb-3.5">
                  <RandomUserImage face={face} size="w-16 h-16 sm:w-20 sm:h-20" className="mx-auto border border-indigo-500/20 shadow-md group-hover:border-indigo-400/40 transition-colors" />
                  {face.isOnline && (
                    <div className="absolute top-0 right-1/3 w-3 h-3 bg-emerald-500 rounded-full border border-slate-900 animate-pulse"></div>
                  )}
                </div>

                {/* Details */}
                <div className="space-y-1.5 flex-grow">
                  <h3 className="font-bold text-xs sm:text-sm text-white group-hover:text-indigo-300 transition-colors">{face.name}</h3>
                  <p className="text-[10px] text-gray-400 font-medium">{face.age} • {face.location}</p>
                  
                  {/* Interests tags */}
                  <div className="flex flex-wrap gap-1 justify-center min-h-[1.5rem] pt-1">
                    {face.interests.slice(0, 2).map((interest, i) => (
                      <span key={i} className="bg-indigo-500/10 text-indigo-300 text-[8px] px-2 py-0.5 rounded-full border border-indigo-500/5">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action button */}
                <button
                  onClick={() => initiateCall(face)}
                  className="w-full bg-slate-950/80 hover:bg-indigo-600 text-indigo-300 hover:text-white mt-4 py-2 rounded-xl text-[10px] sm:text-xs font-bold transition-all border border-indigo-500/15 group-hover:border-indigo-500/40 shadow-sm"
                >
                  Call Now
                </button>
              </div>
            )) : (
              // Skeletal loading place holders
              Array.from({ length: 4 }, (_, index) => (
                <div key={index} className="bg-slate-900/20 p-6 rounded-2xl border border-slate-800 text-center animate-pulse">
                  <div className="w-16 h-16 bg-slate-800 rounded-full mx-auto mb-3"></div>
                  <div className="h-4 bg-slate-800 rounded mb-2"></div>
                  <div className="h-3 bg-slate-800 rounded"></div>
                </div>
              ))
            )}
          </div>

          {/* Refresh Grid */}
          <div className="text-center">
            <button
              onClick={() => {
                setRandomFaces(generateRandomFaces());
                setListeners(generateListeners());
              }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-full text-xs sm:text-sm font-bold shadow-lg shadow-indigo-600/15 transform hover:scale-105 transition-all duration-300 inline-flex items-center space-x-1.5"
            >
              <Phone size={14} />
              <span>Refresh Profiles Grid</span>
            </button>
          </div>
        </div>
      </section>

      {/* Flagship stats */}
      <section className="py-12 bg-slate-950/60 border-y border-slate-900" data-animate>
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-3 gap-4 text-center">
          <div className="space-y-1">
            <div className="text-lg sm:text-3xl font-black text-indigo-400">50,000+</div>
            <div className="text-[10px] sm:text-xs text-gray-400">Happy Users</div>
          </div>
          <div className="space-y-1">
            <div className="text-lg sm:text-3xl font-black text-indigo-400">200,000+</div>
            <div className="text-[10px] sm:text-xs text-gray-400">Connection Minutes</div>
          </div>
          <div className="space-y-1">
            <div className="text-lg sm:text-3xl font-black text-indigo-400">4.7+</div>
            <div className="text-[10px] sm:text-xs text-gray-400">Star Rating</div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 bg-slate-950" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2.5xl sm:text-4xl font-black mb-3 text-white tracking-tight">Features You'll Love</h2>
            <p className="text-xs sm:text-sm text-gray-400">Built to ensure private, empathetic, and secure voice and video communication.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-slate-900/40 p-8 rounded-3xl border border-slate-800 text-center hover:bg-slate-900/60 transition-all duration-300">
              <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-xl flex items-center justify-center mx-auto mb-5 border border-indigo-500/15">
                <Shield size={22} />
              </div>
              <h3 className="font-bold text-sm sm:text-base mb-2 text-white">100% Anonymous</h3>
              <p className="text-xs text-gray-400 leading-relaxed">No phone number sharing or public details. Match and chat privately with strangers.</p>
            </div>

            <div className="bg-slate-900/40 p-8 rounded-3xl border border-slate-800 text-center hover:bg-slate-900/60 transition-all duration-300">
              <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-xl flex items-center justify-center mx-auto mb-5 border border-indigo-500/15">
                <Video size={22} />
              </div>
              <h3 className="font-bold text-sm sm:text-base mb-2 text-white">HD Video Calling</h3>
              <p className="text-xs text-gray-400 leading-relaxed">High-definition voice and video calls ensure crystal-clear connections across India.</p>
            </div>

            <div className="bg-slate-900/40 p-8 rounded-3xl border border-slate-800 text-center hover:bg-slate-900/60 transition-all duration-300">
              <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-xl flex items-center justify-center mx-auto mb-5 border border-indigo-500/15">
                <Users size={22} />
              </div>
              <h3 className="font-bold text-sm sm:text-base mb-2 text-white">Online Listeners</h3>
              <p className="text-xs text-gray-400 leading-relaxed">Get in touch with compassionate listeners. We verify listeners to ensure quality support.</p>
            </div>

            <div className="bg-slate-900/40 p-8 rounded-3xl border border-slate-800 text-center hover:bg-slate-900/60 transition-all duration-300">
              <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-xl flex items-center justify-center mx-auto mb-5 border border-indigo-500/15">
                <Lock size={22} />
              </div>
              <h3 className="font-bold text-sm sm:text-base mb-2 text-white">Safe Environment</h3>
              <p className="text-xs text-gray-400 leading-relaxed">Advanced toxicity scanning filters harmful words and cyberbullying in real-time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Supportive Listeners Section */}
      <section className="py-16 sm:py-20 px-4 bg-slate-900/20 border-t border-slate-900" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2.5xl sm:text-4xl font-black mb-3 text-white tracking-tight">Compassionate Listeners</h2>
            <p className="text-xs sm:text-sm text-gray-400">Share your stories, breakups, or life goals. Listeners are online and here to guide.</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
            {listeners.map((listener, index) => (
              <div key={index} className="bg-slate-900/40 p-4 sm:p-6 rounded-2xl border border-slate-800 text-center hover:bg-slate-900/60 transition-all duration-300">
                <div className="relative mb-3.5">
                  <RandomUserImage face={listener} size="w-16 h-16 sm:w-20 sm:h-20" className="mx-auto" />
                  {listener.online && (
                    <div className="absolute -top-1 -right-1 bg-indigo-600 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                      Online
                    </div>
                  )}
                </div>
                <h3 className="font-bold text-xs sm:text-base text-white mb-2">{listener.name}</h3>
                <div className="flex flex-wrap gap-1 justify-center mb-3">
                  {listener.specialties.map((specialty, i) => (
                    <span key={i} className="bg-indigo-500/10 text-indigo-300 text-[8px] sm:text-[9px] px-2 py-0.5 rounded-full">
                      {specialty}
                    </span>
                  ))}
                </div>
                <p className="text-gray-400 text-[10px] sm:text-xs">Friendly listener ready to support.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 px-4 bg-slate-950 border-t border-slate-900" data-animate>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2.5xl sm:text-4xl font-black text-center mb-12 text-white tracking-tight">Reviews from Play Store</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((review, index) => (
              <div key={index} className="bg-slate-900/45 p-6 rounded-2xl border border-slate-800 flex flex-col justify-between hover:scale-[1.02] transition-transform duration-300">
                <div className="space-y-3.5">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-[10px]">{review.date}</span>
                    <div className="flex text-amber-400 space-x-0.5">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="fill-current" size={12} />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{review.text}</p>
                </div>
                <div className="flex items-center space-x-2 pt-4 border-t border-slate-800/40 mt-4 text-[10px] text-gray-500">
                  <Shield size={12} /> Verified Download
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="faqs" className="py-20 px-4 bg-slate-900/20 border-t border-slate-900 animate-fadeIn" data-animate>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2.5xl sm:text-4xl font-black mb-3 text-white tracking-tight">Frequently Asked Questions</h2>
            <p className="text-gray-400">Get answers to standard questions about the TellMe app structure.</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full p-5 sm:p-6 text-left flex items-center justify-between hover:bg-slate-900/30 transition-colors"
                >
                  <span className="font-bold text-xs sm:text-base text-white pr-4">{faq.question}</span>
                  <ChevronDown className={`transform transition-transform duration-300 text-gray-500 flex-shrink-0 ${activeFaq === index ? 'rotate-180 text-indigo-400' : ''}`} size={18} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${activeFaq === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="p-5 sm:p-6 pt-0 text-gray-400 text-xs sm:text-sm leading-relaxed border-t border-slate-800/20">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA section with play store link */}
      <section className="py-20 bg-gradient-to-br from-indigo-950/80 via-slate-950 to-purple-950/80 text-center relative overflow-hidden" data-animate id="cta">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.08)_0,rgba(0,0,0,0)_60%)]"></div>
        <div className="relative max-w-4xl mx-auto px-4 space-y-7 z-10">
          <Heart className="w-12 h-12 text-indigo-500 mx-auto animate-pulse" />
          <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight">Ready to meet random people?</h2>
          <p className="text-sm sm:text-lg text-gray-400 max-w-lg mx-auto leading-relaxed">
            Download the TellMe voice & video chat app directly on Google Play Store. Connect with verified caring listeners who ready to listen to you.
          </p>
          <a 
            href="https://play.google.com/store/apps/details?id=com.avente.tellme"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-slate-950 hover:bg-gray-100 px-8 py-4 rounded-full font-bold text-xs sm:text-sm shadow-xl inline-flex items-center space-x-1.5 transition-all transform hover:-translate-y-0.5"
          >
            <span>Get TellMe App on Google Play</span>
            <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12 border-t border-slate-900 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div className="space-y-3 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">T</span>
                </div>
                <span className="text-lg font-bold text-white">TellMe</span>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed max-w-xs">
                Bringing People Closer Through Secure Voice & Video Calling. Meet random listeners across India.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-xs sm:text-sm mb-3.5 text-indigo-400 uppercase tracking-wider">Navigation</h3>
              <div className="space-y-2 text-xs text-gray-400">
                <button onClick={() => scrollToSection('home')} className="block hover:text-indigo-400 bg-transparent border-0 cursor-pointer text-left w-full">Home</button>
                <button onClick={() => scrollToSection('online-users')} className="block hover:text-indigo-400 bg-transparent border-0 cursor-pointer text-left w-full">Online Grid</button>
                <button onClick={() => scrollToSection('features')} className="block hover:text-indigo-400 bg-transparent border-0 cursor-pointer text-left w-full">Features</button>
                <button onClick={() => scrollToSection('reviews')} className="block hover:text-indigo-400 bg-transparent border-0 cursor-pointer text-left w-full">Reviews</button>
                <button onClick={() => scrollToSection('faqs')} className="block hover:text-indigo-400 bg-transparent border-0 cursor-pointer text-left w-full">FAQs</button>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-xs sm:text-sm mb-3.5 text-indigo-400 uppercase tracking-wider">Company</h3>
              <div className="space-y-2 text-xs text-gray-400 flex flex-col">
                <Link to="/" className="hover:text-indigo-400">Company Portfolio</Link>
                <Link to="/gupshup-app" className="hover:text-indigo-400">Gupshup App</Link>
                <Link to="/dakhla-app" className="hover:text-indigo-400">Dakhla Video Call App</Link>
                <Link to="/contact-us" className="hover:text-indigo-400">Contact Us</Link>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-xs sm:text-sm mb-3.5 text-indigo-400 uppercase tracking-wider">Support & Office</h3>
              <div className="space-y-2 text-xs text-gray-400">
                <p className="flex items-center gap-1.5"><Mail size={12} /> mail@aventematnitch.in</p>
                <p className="flex items-start gap-1.5"><MapPin size={12} className="mt-0.5 flex-shrink-0" /> Poonch, Jammu & Kashmir, India</p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-900 pt-6 flex flex-col sm:flex-row justify-between items-center text-[10px] text-gray-500 gap-2">
            <p>Copyright © 2026 TellMe (AVENTE MATNITCH PRIVATE LIMITED). All Rights Reserved.</p>
            <p className="flex items-center gap-1">Made with <Heart className="text-red-500 fill-current" size={10} /> for secure conversations</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TellMeApp;
